/* eslint-disable import/prefer-default-export */
import React, { useEffect } from 'react';
import { gql } from '@apollo/client';

import { Loader, LOADER_SIZE } from '@domain-group/fe-blueprint-ui/loader';
import { handleError } from '@domain-group/fe-helper';

import type { ListingFilterStatus } from '../../../../generated/graphql';

import NoListingsMessage from './no-listing-message/no-listing-message';
import type { Choice } from '../../@types';
import CurrentListingCardWithFragment from './current-listing-card/current-listing-card-with-fragment';

import * as styles from './listings-results.style';
import { useContactListingsResults } from '../../hooks';
import { notEmpty } from '../../../../utils';
import AD_COMPONENTS from '../../../../constants/ad-components';

export const CURRENT_LISTING_QUERY = gql`
  query contactCurrentListings(
    $contactId: Int!
    $page: Int!
    $suburbId: String
    $propertyType: AgencyApiPropertyType
    $sortBy: AgentsearchListingsSortBy
    $bedrooms: AgentsearchListingBedrooms
    $imageFormat: ImageFormat
    $quality: Int
  ) {
    contactByContactId(contactId: $contactId) {
      id
      agentsearchListingsByAgentIdV2AndAgencyId(
        page: $page
        pageSize: 6
        listingStatuses: [SOLD, SALE, LEASE, LEASED]
        propertyType: $propertyType
        sortBy: $sortBy
        suburbId: $suburbId
        bedrooms: $bedrooms
      ) {
        soldListings {
          ...ContactListingsTab
        }
        saleListings {
          ...ContactListingsTab
        }
        leaseListings {
          ...ContactListingsTab
        }
        leasedListings {
          ...ContactListingsTab
        }
      }
    }
  }

  fragment ContactListingsTab on AgentsearchListingResults {
    total
    totalPages
    results {
      saleType
      listing {
        ...CurrentListingCard
      }
    }
  }

  ${CurrentListingCardWithFragment.fragment}
`;

export type ContactListingCardsProps = {
  contactId: number;
  listingStatus?: Choice<ListingFilterStatus>;
  propertyType?: string;
  sortBy?: string;
  suburbId?: string;
  bedrooms?: string;
  page: number;
  totalPages: number;
  setTotalPages: (newPage: number) => void;
  onListingCardClick?: () => void;
  mobilePadding?: string | number;
  imageFormat?: string;
  quality?: number;
};

export const ContactListingCards = ({
  contactId,
  page,
  bedrooms,
  listingStatus,
  propertyType,
  sortBy,
  totalPages,
  suburbId,
  setTotalPages,
  onListingCardClick,
  mobilePadding,
  imageFormat,
  quality,
}: ContactListingCardsProps): JSX.Element | null => {
  const variables = {
    contactId,
    bedrooms,
    page,
    propertyType,
    sortBy,
    suburbId,
    imageFormat,
    quality,
  };

  const { listings, loading, error } = useContactListingsResults({
    ...variables,
    listingStatus,
  });

  const newTotalPages = listings?.totalPages;

  useEffect(() => {
    if (Number.isInteger(newTotalPages) && newTotalPages !== totalPages) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setTotalPages(newTotalPages!);
    }
  }, [newTotalPages, setTotalPages, totalPages]);

  if (loading) {
    return (
      <div css={styles.loadingContainer}>
        <Loader size={LOADER_SIZE.LARGE} />
      </div>
    );
  }

  /* istanbul ignore next */
  if (!global.window && process.env.NODE_ENV === 'development' && error) {
    return (
      <div css={styles.errorMessage} data-testid="listings-tabs__error-message">
        <>
          <span style={{ fontWeight: 'bold' }}>ERROR:</span> {error.message}
        </>
      </div>
    );
  }
  if (error) {
    handleError(error);
  }

  // Needs to come after loading or will display this while loading
  if (!listings) {
    return <NoListingsMessage />;
  }

  const listingIds = listings.results
    ?.map((advert) => advert?.listing?.listingId)
    .filter(notEmpty);

  // No listings
  if (!listingIds?.length) {
    return <NoListingsMessage />;
  }

  const listingCards = listingIds.map(
    (id, index) =>
      id && (
        <CurrentListingCardWithFragment
          key={id}
          adId={id}
          adComponent={AD_COMPONENTS.CurrentContactListings}
          index={index}
          onListingCardClick={onListingCardClick}
          imageFormat={imageFormat}
          quality={quality}
        />
      ),
  );

  return (
    <div
      data-testid="listings-tabs__listings-list"
      css={styles.listingsCardsContainer(mobilePadding)}
      id="listing-results"
      role="region"
      aria-live="polite"
    >
      {listingCards}
    </div>
  );
};
